import { useState, useMemo } from "react";

export default function LoanCalculator({ standalone = false }: { standalone?: boolean }) {
  const [loanAmount, setLoanAmount] = useState(10000);
  const [loanTerm, setLoanTerm] = useState(24);
  const [interestRate, setInterestRate] = useState(0.75);

  const { totalPayable } = useMemo(() => {
    const principal = loanAmount;
    const dailyRate = interestRate / 100;
    const totalInterest = principal * dailyRate * loanTerm;
    const totalPayable = principal + totalInterest;
    const apr = ((totalInterest / principal) / loanTerm) * 365 * 100;
    return { totalPayable, apr };
  }, [loanAmount, interestRate, loanTerm]);

  return (
    <section className={`w-full py-8 md:py-4 bg-white ${standalone ? 'pt-28 md:pt-32' : 'pt-14 md:pt-10'}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 md:mb-10">
          <h1 className="text-3xl md:text-4xl lg:text-[48px] lg:leading-[48px] font-bold text-center text-[#2C3E50] font-['Inter'] mb-3 md:mb-4">
            Loan Calculator 
          </h1>
          <p className="text-gray-600 text-lg md:text-[20px] font-['Inter']">
            Plan your loan with clear rates and total payable amount.
          </p>
        </div>

        {/* Calculator Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {/* Left Side - Sliders */}
          <div className="bg-gradient-to-br from-[#A9F9EE] via-[#7DD4D0] to-[#21A6B5] rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-10 shadow-lg">
            {/* Estimated Amount Required */}
            <div className="mb-8 md:mb-10">
              <label className="text-[#2C3E50] font-medium text-sm md:text-base font-['Inter'] mb-2 md:mb-3 block">
                Estimated Amount Required
              </label>
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C3E50] mb-3 md:mb-4 font-['Inter']">
                ₹{loanAmount.toLocaleString('en-IN')}.00
              </div>
              <input
                type="range"
                min="5000"
                max="120000"
                step="1000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3D4A7C ${((loanAmount - 5000) / (120000 - 5000)) * 100}%, #ffffff ${((loanAmount - 5000) / (120000 - 5000)) * 100}%)`,
                }}
              />
              <div className="flex justify-between text-xs md:text-sm text-[#2C3E50] mt-2 font-['Inter']">
                <span>₹5K</span>
                <span>₹1.2L</span>
              </div>
            </div>

            {/* Your Preferred Loan Term */}
            <div className="mb-8 md:mb-10">
              <label className="text-[#2C3E50] font-medium text-sm md:text-base font-['Inter'] mb-2 md:mb-3 block">
                Your Preferred Loan Term (In Days)
              </label>
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C3E50] mb-3 md:mb-4 font-['Inter']">
                {loanTerm} Days
              </div>
              <input
                type="range"
                min="7"
                max="45"
                step="1"
                value={loanTerm}
                onChange={(e) => setLoanTerm(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3D4A7C ${((loanTerm - 7) / (45 - 7)) * 100}%, #ffffff ${((loanTerm - 7) / (45 - 7)) * 100}%)`,
                }}
              />
              <div className="flex justify-between text-xs md:text-sm text-[#2C3E50] mt-2 font-['Inter']">
                <span>7 Days</span>
                <span>45 Days</span>
              </div>
            </div>

            {/* Interest Rate */}
            <div>
              <label className="text-[#2C3E50] font-medium text-sm md:text-base font-['Inter'] mb-2 md:mb-3 block">
                Interest Rate
              </label>
              <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C3E50] mb-3 md:mb-4 font-['Inter']">
                {interestRate.toFixed(2)}% Per Day
              </div>
              <input
                type="range"
                min="0.5"
                max="1"
                step="0.05"
                value={interestRate}
                onChange={(e) => setInterestRate(Number(e.target.value))}
                className="w-full h-2 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3D4A7C ${((interestRate - 0.5) / (1 - 0.5)) * 100}%, #ffffff ${((interestRate - 0.5) / (1 - 0.5)) * 100}%)`,
                }}
              />
              <div className="flex justify-between text-xs md:text-sm text-[#2C3E50] mt-2 font-['Inter']">
                <span>0.5%</span>
                <span>1%</span>
              </div>
            </div>
          </div>

          {/* Right Side - Results */}
          <div className="flex flex-col gap-4 md:gap-6">
            {/* Summary Card */}
            <div className="bg-gradient-to-br from-[#A9F9EE] via-[#7DD4D0] to-[#21A6B5] rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg">
              <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
                <div className="flex justify-between items-center">
                  <span className="text-[#2C3E50] font-medium text-base md:text-lg font-['Inter']">Loan Amount</span>
                  <span className="text-[#2C3E50] font-bold text-lg md:text-xl font-['Inter']">₹ {loanAmount.toLocaleString('en-IN')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#2C3E50] font-medium text-base md:text-lg font-['Inter']">Tenure</span>
                  <span className="text-[#2C3E50] font-bold text-lg md:text-xl font-['Inter']">{loanTerm} Days</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#2C3E50] font-medium text-base md:text-lg font-['Inter']">Interest Rate</span>
                  <span className="text-[#2C3E50] font-bold text-lg md:text-xl font-['Inter']">{interestRate.toFixed(2)}%</span>
                </div>
             
              </div>

              <div className="border-t border-[#2C3E50]/30 pt-4 md:pt-6">
                <div className="text-center mb-3 md:mb-4">
                  <p className="text-[#2C3E50] text-sm md:text-base font-medium font-['Inter'] mb-2">Total Payable</p>
                  <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1B2F7B] font-['Inter']">₹{totalPayable.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <a
                  href={import.meta.env.VITE_WEBSITE_LINK}
                  target="_blank"
                  className="block w-full text-center px-6 md:px-8 py-3 md:py-4 rounded-full bg-[#3D4A7C] text-white font-semibold hover:bg-[#2d3a6c] transition-colors text-sm md:text-base font-['Inter']"
                >
                  Get Funds Now
                </a>
              </div>
            </div>

            {/* Rate & Charges Card */}
            <div className="bg-gradient-to-br from-[#A9F9EE] via-[#7DD4D0] to-[#21A6B5] rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-lg flex-1">
              <h3 className="text-xl md:text-2xl font-bold text-[#2C3E50] mb-4 md:mb-5 font-['Inter']">Rate & Charges</h3>
              <div className="space-y-2.5 md:space-y-3">
                {[
                  "Loan Amount: ₹5,000 to ₹1,20,000",
                  "Tenure: 7 to 45 Days",
                  "No Pre-closure Charges",
                  "No Prepayment Charges",
                  "APR: 15% to 35%",
                  "Net Monthly Salary: ≥ ₹40,000"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-2 md:gap-3">
                    <svg className="w-5 h-5 md:w-6 md:h-6 text-[#2C3E50] mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-[#2C3E50] text-base md:text-lg font-medium font-['Inter']">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
          input[type="range"]::-webkit-slider-thumb {
            appearance: none;
            width: 20px;
            height: 20px;
            background: #3D4A7C;
            border: 3px solid white;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          }
          input[type="range"]::-moz-range-thumb {
            width: 20px;
            height: 20px;
            background: #3D4A7C;
            border: 3px solid white;
            border-radius: 50%;
            cursor: pointer;
            box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          }
        `
      }} />
    </section>
  );
}
